import type { Disposable } from "@/shared/contracts/lifecycle";
import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import { huge } from "@/shared/numbers";
import type { OHovSnapshot } from "../types";
import {
  createOHovHoverConfirmController,
  DEFAULT_OHOV_HOVER_CONFIRM_MS,
  type OHovHoverConfirmController,
} from "./oHovHoverConfirmController";
import { OHOV_UNLOCK_HOV_THRESHOLD } from "./oHovLogic";

interface OHovProgressViewOptions {
  readonly container: HTMLElement;
  readonly onConfirm?: () => void;
  readonly getConfirmDurationMs?: () => number;
}

export interface OHovManualExecutionButtonVisual {
  readonly rect: DOMRect;
  readonly fillColor: string;
}

export class OHovProgressView implements Disposable {
  readonly root: HTMLDivElement;

  private readonly fillEl: HTMLDivElement;
  private readonly percentEl: HTMLDivElement;
  private readonly circleButton: HTMLButtonElement;
  private readonly metricsEl: HTMLDListElement;
  private readonly countValueEl: HTMLSpanElement;
  private readonly powerValueEl: HTMLSpanElement;
  private readonly hovCoreValueEl: HTMLSpanElement;
  private readonly currentRunValueEl: HTMLSpanElement;
  private readonly lastRunValueEl: HTMLSpanElement;
  private readonly fastestRunValueEl: HTMLSpanElement;
  private readonly confirmController: OHovHoverConfirmController;
  private canConfirm = false;

  private readonly handleConfirmStart = (): void => {
    this.confirmController.begin();
  };

  private readonly handleConfirmCancel = (): void => {
    this.confirmController.cancel();
  };

  constructor(options: OHovProgressViewOptions) {
    this.root = document.createElement("div");
    this.root.className = "ohov-progress";

    const trackEl = document.createElement("div");
    trackEl.className = "ohov-progress-track";
    trackEl.setAttribute("role", "progressbar");
    trackEl.setAttribute("aria-valuemin", "0");
    trackEl.setAttribute("aria-valuemax", "100");
    trackEl.setAttribute(
      "aria-label",
      `OHov progress to ${formatForContext(huge(OHOV_UNLOCK_HOV_THRESHOLD), "compact")} Hov`,
    );

    this.fillEl = document.createElement("div");
    this.fillEl.className = "ohov-progress-fill";
    this.fillEl.setAttribute("aria-hidden", "true");

    this.percentEl = document.createElement("div");
    this.percentEl.className = "ohov-progress-percent";

    this.circleButton = document.createElement("button");
    this.circleButton.type = "button";
    this.circleButton.className = "ohov-circle-button";
    this.circleButton.textContent = "";
    this.circleButton.setAttribute("aria-label", "OHov");
    this.circleButton.dataset.virtualPointer = "allow";
    this.circleButton.disabled = true;
    this.circleButton.hidden = true;
    this.circleButton.style.setProperty("--ohov-confirm-progress", "0");

    this.metricsEl = document.createElement("dl");
    this.metricsEl.className = "ohov-metrics";
    this.metricsEl.hidden = true;
    this.countValueEl = appendMetric(this.metricsEl, "OHov", "ohov-count");
    this.powerValueEl = appendMetric(this.metricsEl, "Power", "ohov-power");
    this.hovCoreValueEl = appendMetric(
      this.metricsEl,
      "Hov Core",
      "ohov-hov-core",
    );
    this.currentRunValueEl = appendMetric(
      this.metricsEl,
      "Current",
      "ohov-current",
    );
    this.lastRunValueEl = appendMetric(this.metricsEl, "Last", "ohov-last");
    this.fastestRunValueEl = appendMetric(
      this.metricsEl,
      "Best",
      "ohov-fastest",
    );

    this.confirmController = createOHovHoverConfirmController({
      getConfirmDurationMs: () =>
        options.getConfirmDurationMs?.() ?? DEFAULT_OHOV_HOVER_CONFIRM_MS,
      isConfirmable: () => this.canConfirm,
      onProgress: (progress) => {
        this.circleButton.style.setProperty(
          "--ohov-confirm-progress",
          String(progress),
        );
      },
      onComplete: () => {
        options.onConfirm?.();
      },
    });

    trackEl.append(this.fillEl, this.percentEl);
    this.root.append(trackEl, this.circleButton, this.metricsEl);
    options.container.append(this.root);

    this.circleButton.addEventListener("pointerenter", this.handleConfirmStart);
    this.circleButton.addEventListener("mouseenter", this.handleConfirmStart);
    this.circleButton.addEventListener("focus", this.handleConfirmStart);
    this.circleButton.addEventListener(
      "pointerleave",
      this.handleConfirmCancel,
    );
    this.circleButton.addEventListener("mouseleave", this.handleConfirmCancel);
    this.circleButton.addEventListener(
      "pointercancel",
      this.handleConfirmCancel,
    );
    this.circleButton.addEventListener("blur", this.handleConfirmCancel);
  }

  update(snapshot: OHovSnapshot): void {
    const pct = Math.round(snapshot.progressRatio * 1000) / 10;
    const pctText = String(Math.max(0, Math.min(100, pct)));

    this.fillEl.style.width = `${pctText}%`;
    this.percentEl.textContent = snapshot.gaugeText;
    const trackEl = this.root.querySelector<HTMLElement>(
      ".ohov-progress-track",
    );
    trackEl?.setAttribute("aria-valuenow", pctText);
    trackEl?.setAttribute(
      "aria-label",
      `OHov progress to ${formatForContext(huge(snapshot.executeTargetHov ?? OHOV_UNLOCK_HOV_THRESHOLD), "compact")} Hov`,
    );
    this.canConfirm = snapshot.canManualExecute;
    this.circleButton.disabled = !snapshot.canManualExecute;
    this.circleButton.hidden = !snapshot.canManualExecute;
    this.circleButton.dataset.canExecute = snapshot.canManualExecute
      ? "true"
      : "false";
    this.confirmController.syncConfirmable();

    this.metricsEl.hidden = true;
    this.countValueEl.textContent = String(snapshot.count);
    this.powerValueEl.textContent = snapshot.powerLabel;
    this.hovCoreValueEl.textContent = snapshot.hovCoreLabel;
    this.currentRunValueEl.textContent = snapshot.currentRunTimeLabel;
    this.lastRunValueEl.textContent = snapshot.lastRunTimeLabel;
    this.fastestRunValueEl.textContent = snapshot.fastestRunTimeLabel;
  }

  getManualExecutionButtonVisual(): OHovManualExecutionButtonVisual {
    if (!this.circleButton.hidden) {
      return this.readManualExecutionButtonVisual();
    }

    const previousVisibility =
      this.circleButton.style.getPropertyValue("visibility");
    // `hidden` removes the button's layout box. Reveal it invisibly within this
    // synchronous measurement so a concealed button still uses its real CSS
    // position without painting or accepting input.
    this.circleButton.style.setProperty("visibility", "hidden");
    this.circleButton.hidden = false;
    try {
      return this.readManualExecutionButtonVisual();
    } finally {
      this.circleButton.hidden = true;
      if (previousVisibility === "") {
        this.circleButton.style.removeProperty("visibility");
      } else {
        this.circleButton.style.setProperty("visibility", previousVisibility);
      }
    }
  }

  private readManualExecutionButtonVisual(): OHovManualExecutionButtonVisual {
    return {
      rect: this.circleButton.getBoundingClientRect(),
      fillColor: getComputedStyle(this.circleButton)
        .getPropertyValue("--ohov-button-fill-color")
        .trim(),
    };
  }

  dispose(): void {
    this.confirmController.dispose();
    this.circleButton.removeEventListener(
      "pointerenter",
      this.handleConfirmStart,
    );
    this.circleButton.removeEventListener(
      "mouseenter",
      this.handleConfirmStart,
    );
    this.circleButton.removeEventListener("focus", this.handleConfirmStart);
    this.circleButton.removeEventListener(
      "pointerleave",
      this.handleConfirmCancel,
    );
    this.circleButton.removeEventListener(
      "mouseleave",
      this.handleConfirmCancel,
    );
    this.circleButton.removeEventListener(
      "pointercancel",
      this.handleConfirmCancel,
    );
    this.circleButton.removeEventListener("blur", this.handleConfirmCancel);
    this.root.remove();
  }
}

function appendMetric(
  container: HTMLElement,
  label: string,
  modifier: string,
): HTMLSpanElement {
  const wrapper = document.createElement("div");
  wrapper.className = `ohov-metric ohov-metric--${modifier}`;

  const dt = document.createElement("dt");
  dt.className = "ohov-metric-label";
  dt.textContent = label;

  const dd = document.createElement("dd");
  dd.className = "ohov-metric-value";
  dd.textContent = "--";

  wrapper.append(dt, dd);
  container.append(wrapper);
  return dd;
}
